import {
  Activity, ArrowLeft, ArrowUpRight, Building2, ClipboardList, FileClock, Handshake, Images, LayoutDashboard, LogOut, Map, Moon, Package, PiggyBank, Plane, Scale, Search, Settings, Sun, Users
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { Logo } from "../../components/Logo";
import { PageHeaderProvider, usePageHeaderValue } from "../../components/shell/PageHeaderContext";
import { useAdminOverview } from "../../lib/queries";
import { initials } from "../../lib/format";
import { useTheme } from "../../theme/ThemeContext";

interface NavItem { to: string; label: string; icon: typeof Users; end?: boolean; badge?: number; badgeTone?: "alert" | "danger" }
interface NavGroup { label: string | null; items: NavItem[] }

/**
 * The administration frame. Its own shell rather than a section of the customer app: the
 * navigation is grouped by job (customers, integrations, content, system), the numbers that need
 * attention travel as badges, and the topbar carries a search that jumps straight to a customer.
 */
export function AdminShell() {
  return (
    <PageHeaderProvider>
      <AdminFrame />
    </PageHeaderProvider>
  );
}

function AdminFrame() {
  const { user, logout } = useAuth();
  const header = usePageHeaderValue();
  const { pathname } = useLocation();
  const overview = useAdminOverview();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  const kuspitIssues = overview.data ? overview.data.kuspit.syncFailed + overview.data.kuspit.failedNotifications + overview.data.kuspit.stuckWallets : 0;
  const groups: NavGroup[] = [
    { label: null, items: [{ to: "/admin", label: "Resumen", icon: LayoutDashboard, end: true, badge: overview.data?.attention.length, badgeTone: overview.data?.attention.some((item) => item.severity === "danger") ? "danger" : "alert" }] },
    { label: "Clientes", items: [{ to: "/admin/usuarios", label: "Usuarios", icon: Users, badge: overview.data?.counts.pending_kyc, badgeTone: "alert" }] },
    {
      label: "Integraciones",
      items: [
        { to: "/admin/kuspit", label: "Kuspit", icon: Building2, badge: kuspitIssues, badgeTone: overview.data && overview.data.kuspit.syncFailed + overview.data.kuspit.failedNotifications > 0 ? "danger" : "alert" },
        { to: "/admin/fondos", label: "Fondos", icon: Scale, badge: overview.data?.fund?.openBreaks, badgeTone: "danger" },
        { to: "/admin/peso-a-peso", label: "Peso a Peso", icon: Handshake, badge: overview.data?.counts.open_loans, badgeTone: "alert" }
      ]
    },
    {
      label: "Viajes",
      items: [
        { to: "/admin/viajes", label: "Viajes", icon: Plane },
        { to: "/admin/solicitudes", label: "Solicitudes", icon: ClipboardList, badge: overview.data?.counts.open_requests, badgeTone: "alert" },
        { to: "/admin/apartados", label: "Apartados", icon: PiggyBank, badge: overview.data?.counts.investment_review, badgeTone: "danger" }
      ]
    },
    { label: "Contenido", items: [{ to: "/admin/fotos", label: "Fotos", icon: Images }, { to: "/admin/destinos", label: "Destinos", icon: Map }, { to: "/admin/productos", label: "Productos", icon: Package }] },
    { label: "Sistema", items: [{ to: "/admin/bitacora", label: "Bitácora", icon: FileClock }, { to: "/admin/eventos", label: "Eventos", icon: Activity }, { to: "/admin/ajustes", label: "Ajustes", icon: Settings }] }
  ];
  const flat = groups.flatMap((group) => group.items);

  return (
    <div className="app-shell admin-shell">
      <aside className="side-nav" aria-label="Administración">
        <div className="side-brand admin-brand">
          <Link to="/admin" className="side-brand-link" aria-label="Resumen de administración"><Logo height={24} /></Link>
          <span className="admin-brand-tag">Administración</span>
        </div>
        <nav>
          {groups.map((group, index) => (
            <div key={group.label ?? index} className="nav-block">
              {group.label && <span className="nav-group">{group.label}</span>}
              {group.items.map((item) => <SideLink key={item.to} item={item} />)}
            </div>
          ))}
          <span className="nav-group">Ir a</span>
          <Link to="/inicio" className="nav-item nav-item-external"><ArrowUpRight aria-hidden="true" size={18} /><span>App de clientes</span></Link>
          <Link to="/aliados" className="nav-item nav-item-external"><ArrowUpRight aria-hidden="true" size={18} /><span>Portal de aliados</span></Link>
        </nav>
        <div className="side-account">
          <NavLink to="/perfil" className="side-account-link" aria-label="Mi cuenta">
            <div className="avatar" aria-hidden="true">{initials(user?.fullName)}</div>
            <div className="account-copy"><strong>{user?.fullName}</strong><span>Administrador · Aventuria</span></div>
          </NavLink>
          <button className="icon-button ghost" onClick={() => void logout()} aria-label="Cerrar sesión" title="Cerrar sesión">
            <LogOut size={17} aria-hidden="true" />
          </button>
        </div>
      </aside>

      <div className="app-content">
        <header className="topbar admin-topbar">
          {pathname !== "/admin" && <BackButton />}
          <div className="topbar-copy">
            <h1>{header.title}</h1>
            {header.subtitle && <span className="topbar-subtitle">{header.subtitle}</span>}
          </div>
          <div className="topbar-actions">
            {header.actions}
            <CustomerSearch />
            <ThemeToggle />
          </div>
        </header>
        <nav className="admin-mobile-nav" aria-label="Secciones de administración">
          {flat.map(({ to, label, end, badge }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `admin-mobile-tab${isActive ? " active" : ""}`}>
              {label}{badge ? <span className="nav-badge">{badge}</span> : null}
            </NavLink>
          ))}
          <Link to="/inicio" className="admin-mobile-tab">App</Link>
        </nav>
        <main className="app-main"><Outlet /></main>
      </div>
    </div>
  );
}

function SideLink({ item }: { item: NavItem }) {
  const { to, label, icon: Icon, end, badge, badgeTone } = item;
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
      <Icon aria-hidden="true" size={18} />
      <span>{label}</span>
      {badge ? <span className={`nav-badge ${badgeTone ?? "alert"}`} aria-label={`${badge} pendientes`}>{badge}</span> : null}
    </NavLink>
  );
}

function BackButton() {
  const navigate = useNavigate();
  const goBack = () => {
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (index > 0) navigate(-1);
    else navigate("/admin");
  };
  return (
    <button type="button" className="topbar-button topbar-back" onClick={goBack} aria-label="Regresar" title="Regresar">
      <ArrowLeft size={18} aria-hidden="true" />
    </button>
  );
}

function ThemeToggle() {
  const { resolved, toggle } = useTheme();
  const dark = resolved === "dark";
  return (
    <button type="button" className="topbar-button" onClick={toggle} aria-label={dark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"} title={dark ? "Modo claro" : "Modo oscuro"} aria-pressed={dark}>
      {dark ? <Sun size={17} aria-hidden="true" /> : <Moon size={17} aria-hidden="true" />}
    </button>
  );
}

/** Type a name, e-mail, contract or CLABE and land on the customer list already filtered. */
function CustomerSearch() {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const query = value.trim();
    if (!query) return;
    navigate(/^\d{6,}$/.test(query) ? `/admin/kuspit/contratos?q=${encodeURIComponent(query)}` : `/admin/usuarios?q=${encodeURIComponent(query)}`);
    setValue("");
  };
  return (
    <form className="admin-search" role="search" onSubmit={submit}>
      <Search size={15} aria-hidden="true" />
      <input type="search" value={value} onChange={(event) => setValue(event.target.value)} placeholder="Buscar cliente, contrato o CLABE" aria-label="Buscar cliente, contrato o CLABE" />
    </form>
  );
}
